/**
 * Kontowechsel („Mit anderem Konto anmelden").
 *
 * Warum das nötig ist: `signOut()` beendet nur die next-auth-Session (Cookie auf
 * der CRM-Domain). Die Keycloak-SSO-Session lebt weiter, der nächste Login läuft
 * still mit demselben Konto durch. `prompt=login` allein reicht nicht — Keycloak
 * zeigt dann zwar eine Anmeldemaske, aber die ist an den bereits erkannten
 * Benutzer gebunden („Zum Fortsetzen bitte erneut anmelden"); ein fremdes Konto
 * quittiert es mit „falsches Passwort". Ein echter Wechsel verlangt, die
 * SSO-Session per RP-initiated Logout zu beenden.
 *
 * Ablauf (ein Klick):
 *   1. Button  -> ACCOUNT_SWITCH_START_PATH (Route Handler, serverseitig)
 *   2. Handler -> Keycloak end_session_endpoint (beendet die SSO-Session)
 *   3. Keycloak -> ACCOUNT_SWITCH_RETURN_PATH
 *   4. Seite   -> signOut() + signIn() -> frische Anmeldemaske
 *
 * Nicht zu verwechseln mit dem Admin-Cross-Login (`CustomerContextPanel`), das
 * innerhalb desselben Kontos den Debitor wechselt.
 *
 * Diese Datei ist bewusst frei von Client-Only-Importen, damit der Route
 * Handler sie serverseitig nutzen kann. Die Navigationen liegen in
 * `switch-account-client.ts`.
 */

/** Startpunkt des Wechsels — Route Handler, der zum Keycloak-Logout weiterleitet. */
export const ACCOUNT_SWITCH_START_PATH = '/api/switch-account';

/** Rückkehrziel nach dem Keycloak-Logout; löst dort den neuen Login aus. */
export const ACCOUNT_SWITCH_RETURN_PATH = '/kontowechsel';

export interface AccountSwitchLogoutUrlOptions {
  /** `AUTH_KEYCLOAK_ISSUER`, z. B. https://auth2.info/auth/realms/paeffgen */
  issuer: string;
  /** Origin der App, z. B. https://local-dev.dachbaustoffe.de */
  origin: string;
  /** ID-Token der laufenden Session; ohne das verlangt Keycloak eine Bestätigung. */
  idToken?: string;
  /** `AUTH_KEYCLOAK_ID` — Fallback, wenn kein ID-Token vorliegt. */
  clientId?: string;
}

/**
 * Baut die Keycloak-Logout-URL für den Kontowechsel.
 *
 * Mit `id_token_hint` meldet Keycloak direkt ab und leitet zurück. Fehlt das
 * ID-Token (Sessions, die vor der Einführung dieses Felds entstanden sind),
 * identifiziert `client_id` wenigstens den Client — Keycloak schiebt dann eine
 * Abmelde-Bestätigungsseite dazwischen, der Wechsel funktioniert aber.
 */
export function buildAccountSwitchLogoutUrl({
  issuer,
  origin,
  idToken,
  clientId,
}: AccountSwitchLogoutUrlOptions): string {
  const url = new URL(`${issuer.replace(/\/$/, '')}/protocol/openid-connect/logout`);
  url.searchParams.set(
    'post_logout_redirect_uri',
    new URL(ACCOUNT_SWITCH_RETURN_PATH, origin).toString(),
  );

  if (idToken) {
    url.searchParams.set('id_token_hint', idToken);
  } else if (clientId) {
    url.searchParams.set('client_id', clientId);
  }

  return url.toString();
}
